<template>
  <Drawer
    :id="drawerId"
    title="Project Details"
    :loading="loading"
    padded
  >
    <div>
      <!-- Show any rejection note that may exist -->
      <v-row
        v-if="project.rejectionNotes && !completed"
        dense
        class="mb-3"
      >
        <v-col cols="12">
          <v-alert
            text
            dense
            outlined
            color="error"
            class="body-1 mb-0"
          >
            <div class="d-flex justify-space-between align-center">
              <div>
                Additional work required.
              </div>
              <v-btn
                text
                @click="showRejectionNote"
              >
                View
              </v-btn>
            </div>
          </v-alert>
        </v-col>
      </v-row>

      <!-- Basic project details -->
      <div class="mb-5">
        <div class="text-h6 text-truncate">
          {{ project.name }}
        </div>

        <IconList
          :items="iconList"
          class="mt-2"
        />

        <div v-if="project.notes">
          <div class="text-h6 mt-4">
            Notes
          </div>
          <div class="body-2">
            {{ project.notes }}
          </div>
        </div>
      </div>
      <v-divider class="my-5" />

      <!-- Upload Row -->
      <div v-if="(claimed || underReview) && !completed">
        <v-row
          no-gutters
          align="center"
        >
          <v-col
            cols="12"
          >
            <v-file-input
              v-if="claimed || (underReview && project.rejectionNotes)"
              v-model="file"
              dense
              outlined
              color="primary"
              accept=".zip"
              label="Upload Zip File Here"
              hide-details="auto"
              class="body-2"
            >
              <template v-slot:append-outer>
                <div class="align-center">
                  <v-btn
                    v-if="file !== null"
                    height="38"
                    class="mt-n2"
                    elevation="0"
                    color="primary"
                    :loading="processing"
                    @click="upload"
                  >
                    Upload<v-icon class="ml-2">
                      {{ uploadIcon }}
                    </v-icon>
                  </v-btn>
                </div>
              </template>
            </v-file-input>
          </v-col>
        </v-row>
        <v-divider class="mt-5 mb-2" />
      </div>

      <!-- Row for releasing-->
      <v-row
        v-if="!completed"
        no-gutters
        align="center"
      >
        <v-col
          v-if="claimed"
          cols="12"
        >
          <v-btn
            color="error"
            elevation="0"
            block
            class="mr-3"
            large
            rounded
            :disabled="processing"
            @click="release"
          >
            Release
          </v-btn>
        </v-col>
      </v-row>

      <!-- Claim button-->
      <v-row
        v-if="!claimed && !completed"
        no-gutters
      >
        <v-col
          v-if="available"
          cols="12"
        >
          <v-btn
            color="primary"
            elevation="0"
            block
            class="my-5"
            large
            rounded
            :disabled="processing"
            @click="claim"
          >
            Claim
          </v-btn>
        </v-col>
      </v-row>

      <v-row
        no-gutters
      >
        <v-col
          cols="12"
        >
          <div class="pa-4 bg-light bordered border-light rounded my-3">
            <v-img
              :src="project.sourceSvgUrl"
              max-width="100%"
            />
          </div>
          <v-alert
            color="primary"
            outlined
            class="body-2"
          >
            *** Right click -> Save As to download original SVG
          </v-alert>
        </v-col>
      </v-row>
    </div>
  </Drawer>
</template>

<script>
import { ASSET_PROJECT_STATUS, DRAWERS } from '../../../utils/constants';
import Drawer from '../../layout/Drawer';
import { cloneDeep } from 'lodash';
import IconList from '../../utilities/IconList';
import { mdiClockOutline, mdiCurrencyUsd, mdiCloudUpload } from '@mdi/js';

export default {
  name: 'AssetProjectDrawer',

  components: {
    Drawer,
    IconList
  },

  data () {
    return {
      project: {},
      loading: false,
      drawerId: DRAWERS.CONTRIBUTOR_ASSET_PROJECT_DRAWER,
      processing: false,
      uploadIcon: mdiCloudUpload,
      file: null
    };
  },

  computed: {
    drawerOpenedAt () {
      return this.$store.state.drawers.lastOpenedAt;
    },

    claimed () {
      return this.project.statusId === ASSET_PROJECT_STATUS.ASSIGNED;
    },

    available () {
      return this.project.statusId === ASSET_PROJECT_STATUS.OPEN;
    },

    underReview () {
      return this.project.statusId === ASSET_PROJECT_STATUS.UNDER_REVIEW;
    },

    completed () {
      return this.project.paid;
    },

    iconList () {
      var list = [{
        text: this.$options.filters.shortDateWithTime(this.project.modifiedDate),
        icon: mdiClockOutline
      }, {
        text: this.$options.filters.currency(this.project.price),
        icon: mdiCurrencyUsd
      }];
      return list;
    }
  },

  watch: {
    drawerOpenedAt (val) {
      if (this.drawerId === this.$store.getters['drawers/topDrawer']?.id) {
        this.loading = true;
        this.$store.dispatch('contributorAssetProjects/getDetail', this.$store.state.contributorAssetProjects.selectedProjectId)
          .then(project => {
            this.project = project;
          })
          .finally(() => {
            setTimeout(() => {
              this.loading = false;
            }, 500);
          });
      }
    }
  },

  methods: {
    release () {
      this.processing = true;
      this.$store.dispatch('contributorAssetProjects/release', this.project.id)
        .then((project) => {
          this.project = Object.assign(project, cloneDeep(this.project));
          this.$store.commit('drawers/CLOSE_DRAWER', this.drawerId);
        })
        .finally(() => {
          this.processing = false;
        });
    },

    claim () {
      this.processing = true;
      this.$store.dispatch('contributorAssetProjects/claim', this.project.id)
        .then((project) => {
          this.project = Object.assign(project, cloneDeep(this.project));
          this.$store.commit('drawers/CLOSE_DRAWER', this.drawerId);
        })
        .finally(() => {
          this.processing = false;
        });
    },

    upload () {
      this.processing = true;
      this.$store.dispatch('contributorAssetProjects/uploadZip', {
        id: this.project.id,
        file: this.file
      })
        .then((project) => {
          this.project = Object.assign(project, cloneDeep(this.project));
          this.$store.commit('drawers/CLOSE_DRAWER', this.drawerId);
        })
        .finally(() => {
          this.processing = false;
        });
    },

    showRejectionNote () {
      this.$root.$alert('Errors Found', this.project.rejectionNotes, { hideSupport: true, cancelText: '' });
    }
  }
};
</script>

  <style scoped>
  </style>
